import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { registerUnauthorizedHandler } from '@/api/authSession';

/**
 * Interface representing the User object.
 */
export interface Parent {
    id: number;
    fullName: string;
    phone: string;
    avatar?: string;
    email?: string;
}

/**
 * Interface for the AuthContext state and actions.
 */
interface AuthContextType {
    parent: Parent | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (token: string, userData: Parent) => void;
    logout: () => void;
    updateUser: (userData: Partial<Parent>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<Parent | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    useEffect(() => {
        const initAuth = () => {
            const token = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');

            if (token && storedUser) {
                try {
                    setUser(JSON.parse(storedUser));
                    setIsAuthenticated(true);
                } catch (error) {
                    console.error('Failed to parse stored user:', error);
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                }
            }
            setIsLoading(false);
        };

        initAuth();
    }, []);

    const login = useCallback((token: string, parent: Parent) => {
        console.log("AuthContext: login function called with", { token: '***', parent });
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(parent));
        setUser(parent);
        setIsAuthenticated(true);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
    }, []);

    // The API returns 401 when the backend drops the oldest session (an account
    // opened more than 5 times concurrently); log the parent out so they can sign in again.
    useEffect(() => registerUnauthorizedHandler(logout), [logout]);

    const updateUser = useCallback((userData: Partial<Parent>) => {
        setUser((prevUser) => {
            if (!prevUser) return null;
            const updatedUser = { ...prevUser, ...userData };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            return updatedUser;
        });
    }, []);

    const value = {
        parent: user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        updateUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};



export default AuthContext;